/**
 * Demo-data toggle for the owner ops area.
 *
 * Demo mode is a READ-SIDE OVERLAY ONLY. Turning it on makes every ops fetch
 * append `?demo=1`; the server then answers from a deterministic synthetic
 * dataset and returns BEFORE touching D1, and refuses every write with 409.
 * Nothing is ever inserted, so there is no demo row to leak into a real total
 * and nothing to clean up when it is switched off — the next fetch simply
 * stops sending the flag and returns untouched real data.
 *
 * The flag lives in sessionStorage on purpose: a demo left on by accident dies
 * with the tab rather than following the owner into their next real session.
 */

import { useCallback, useEffect, useState } from 'react';

export const DEMO_STORAGE_KEY = 'tcp_demo_mode';

/** Fired on the window whenever the flag changes, so every mounted page reacts. */
const DEMO_EVENT = 'tcp:demo-mode';

function canStore(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.sessionStorage;
  } catch {
    // Safari in private mode throws on sessionStorage access.
    return false;
  }
}

export function isDemo(): boolean {
  if (!canStore()) return false;
  try {
    return window.sessionStorage.getItem(DEMO_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setDemo(on: boolean): void {
  if (canStore()) {
    try {
      if (on) window.sessionStorage.setItem(DEMO_STORAGE_KEY, '1');
      else window.sessionStorage.removeItem(DEMO_STORAGE_KEY);
    } catch {/* non-fatal — the flag just won't survive a reload */}
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<boolean>(DEMO_EVENT, { detail: on }));
  }
}

export function toggleDemo(): boolean {
  const next = !isDemo();
  setDemo(next);
  return next;
}

/**
 * Append the demo flag to a URL, respecting any query string already present.
 * Returns the URL untouched when demo is off, so real requests are byte-identical
 * to what they were before this feature existed.
 */
export function withDemo(url: string, demo: boolean): string {
  if (!demo) return url;
  return `${url}${url.includes('?') ? '&' : '?'}demo=1`;
}

/** React binding. Every consumer stays in sync via the window event above. */
export function useDemoMode(): [boolean, (on: boolean) => void] {
  const [on, setOn] = useState<boolean>(() => isDemo());

  useEffect(() => {
    const sync = () => setOn(isDemo());
    window.addEventListener(DEMO_EVENT, sync);
    // Another tab in the same session can't change sessionStorage, but a
    // same-tab reload can — re-read on focus so the banner never lies.
    window.addEventListener('focus', sync);
    return () => {
      window.removeEventListener(DEMO_EVENT, sync);
      window.removeEventListener('focus', sync);
    };
  }, []);

  const set = useCallback((next: boolean) => setDemo(next), []);
  return [on, set];
}
